import * as RadPopover from '@radix-ui/react-popover'
import { useAtom } from '@reatom/npm-react'
import React, { forwardRef, useCallback, useState } from 'react'
import styled, { keyframes } from 'styled-components'

import { Typography, useHapticFeedback } from '@blade/ui-kit'
import { ExploreTypeMap } from '@root/features/explore/model/explore-feed-type'
import { useTranslation } from '@root/shared/localization'
import { ArrowDown2Icon, ArrowUp2Icon } from '@root/shared/ui-kit'

import { exploreTabAtom } from '../../atoms/explore-tab-atom'

import type { ExploreFeedType } from '@root/features/explore/model/explore-feed-type'

type ExploreScreenTabsSelectProps = {
  hiddenFeeds: Array<ExploreFeedType>
  onSelect: (feed: ExploreFeedType) => void
}

export const ExploreScreenTabsSelect = forwardRef<
  HTMLButtonElement,
  ExploreScreenTabsSelectProps
>(({ hiddenFeeds, onSelect }, ref) => {
  const { t } = useTranslation()
  const [tab] = useAtom(exploreTabAtom)
  const [open, setOpen] = useState(false)
  const haptics = useHapticFeedback()

  const handleSelect = useCallback(
    (feed: ExploreFeedType) => {
      haptics.impactOccurred('light')
      onSelect(feed)
      setOpen(false)
    },
    [haptics, onSelect],
  )

  const handleTriggerClick = useCallback(() => {
    haptics.impactOccurred('light')
    setOpen((x) => !x)
  }, [haptics])

  const value = ExploreTypeMap[tab]

  return (
    <Root open={open} onOpenChange={setOpen}>
      <Trigger asChild>
        <Button
          ref={ref}
          type="button"
          onClick={handleTriggerClick}
          style={{
            '--item-color': value.color,
          }}
        >
          <ArrowIcon>
            <ArrowUp2Icon size={12} aria-expanded={open} />
            <ArrowDown2Icon size={12} />
          </ArrowIcon>
        </Button>
      </Trigger>
      <RadPopover.Portal>
        <Content side="bottom" align="end">
          {hiddenFeeds.map((feed) => {
            const itemData = ExploreTypeMap[feed]

            return (
              <Item
                key={feed}
                aria-current={feed === tab ? true : undefined}
                onClick={() => handleSelect(feed)}
                style={{
                  '--item-color': itemData.color,
                }}
              >
                <itemData.Icon size={16} /> {t(itemData.label)}
              </Item>
            )
          })}
        </Content>
      </RadPopover.Portal>
    </Root>
  )
})

ExploreScreenTabsSelect.displayName = 'ExploreScreenTabsSelect'

const contentShow = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const Root = styled(RadPopover.Root)``
const Trigger = styled(RadPopover.Trigger)``

const Button = styled.button`
  all: unset;

  background: var(--backgroundA-2);
  gap: 5px;

  display: grid;
  align-items: center;
  cursor: pointer;
  width: 28px;
  height: 28px;

  border-radius: 6px;

  position: absolute;
  right: 12px;
  top: 1px;
  transition: all 0.15s ease-in-out;

  > svg {
    color: var(--item-color);
  }
  &:hover {
    background: var(--backgroundA-2);
  }
`

const ArrowIcon = styled.div`
  display: grid;
  place-items: center;

  color: var(--text-9);

  svg {
    margin-bottom: -1px;
    margin-top: -1px;

    transition: all 0.15s ease-in-out;

    &[aria-expanded='true'] {
      color: var(--text-12);
    }
  }
`

const Content = styled(RadPopover.Content)`
  border-radius: 8px;
  background: var(--backgroundA-1);
  backdrop-filter: blur(40px);

  box-shadow: 2px 2px 9px 1px rgb(0 0 0 / 30%);
  z-index: 2;
  padding: 2px;
  gap: 2px;
  display: grid;
  grid-template-columns: 1fr;

  animation-duration: 200ms;
  animation-name: ${contentShow};
`

const Item = styled(Typography).attrs({
  variant: 'body5-medium',
  component: 'div',
})`
  background: var(--backgroundA-2);
  border-radius: 6px;

  color: var(--text-9);

  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 5px;
  padding: 5px 7px;
  height: 30px;

  transition: all 0.15s ease-in-out;

  svg {
    transition: all 0.15s ease-in-out;
    color: var(--text-9);
  }

  &:hover {
    cursor: pointer;
    color: var(--text-12);
    background: var(--backgroundA-3);
    svg {
      color: var(--item-color);
    }
  }

  &[aria-current='true'] {
    color: var(--text-12);

    svg {
      color: var(--item-color);
    }
  }
  &[aria-active='true'] {
    background: var(--backgroundA-3);
  }
  &[aria-inactive] {
    background: transparent;
    border: 1px solid var(--backgroundA-2);
    &:hover {
      background: var(--backgroundA-3);
    }
  }
`
